// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { z } from 'zod';

// CaseInsensitiveEnum is the FE's wire-format guard for enum fields
// the BE may send in either casing. Storage on the BE is lowercase
// (`monthly`); MarshalJSON uppercases on the wire (`MONTHLY`); but
// some legacy paths (e.g. `util.Nullable[T].MarshalJSON` on a value
// receiver, sparse-update fall-throughs) leak the lowercase form
// onto the wire. Without this wrapper a single legacy emitter would
// surface as a parseResponse error and break the page; with it, the
// FE coerces to the canonical casing and the row renders.
//
// Exposes `.options` (the canonical-case values array) so consumers
// that enumerate the schema (e.g. building filter chips) keep
// working the same way they would on a plain `z.enum`.
//
// The generic uses `readonly [string, ...string[]]` so TypeScript
// preserves the literal union (`'PENDING' | 'CONFIRMED' | ...`)
// instead of widening to `string`. Callers pass an array literal
// without `as const` — the function signature pins the readonly
// tuple at the boundary.
type CaseInsensitiveEnumSchema<T extends readonly [string, ...string[]]> =
  z.ZodEffects<z.ZodEnum<[T[number], ...T[number][]]>, T[number], unknown> & {
    options: T;
  };

export function caseInsensitiveEnum<
  const T extends readonly [string, ...string[]],
>(values: T): CaseInsensitiveEnumSchema<T> {
  const schema = z.preprocess(
    (value) => (typeof value === 'string' ? value.toUpperCase() : value),
    z.enum([...values] as [T[number], ...T[number][]])
  ) as CaseInsensitiveEnumSchema<T>;
  schema.options = values;
  return schema;
}

// CaseInsensitiveLowerEnum is the mirror of `caseInsensitiveEnum` for schemas
// whose canonical frontend value is lowercase. The backend writes
// SCREAMING_CASE on the wire; these schemas coerce it down to the casing the
// components, labels and fixtures already use, so a backend casing change
// does not ripple past the schema boundary.
type CaseInsensitiveLowerEnumSchema<T extends readonly [string, ...string[]]> =
  z.ZodEffects<z.ZodEnum<[T[number], ...T[number][]]>, T[number], unknown> & {
    options: T;
  };

export function caseInsensitiveLowerEnum<
  const T extends readonly [string, ...string[]],
>(values: T): CaseInsensitiveLowerEnumSchema<T> {
  const schema = z.preprocess(
    (value) => (typeof value === 'string' ? value.toLowerCase() : value),
    z.enum([...values] as [T[number], ...T[number][]])
  ) as CaseInsensitiveLowerEnumSchema<T>;
  schema.options = values;
  return schema;
}
